import {
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  TableProps,
  Typography,
  message,
} from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as XLSX from 'xlsx'

import { getClassRecordAttendanceDetails } from '~/services/class-record'
import {
  IAdminTeacherAttendanceDetailItem,
  IAdminTeacherAttendanceDetailsRes,
} from '~/types/class-record'

import { Container } from './style'

/** 薪资口径的结算结果（后端 TeacherSalaryPolicy） */
const SALARY_OUTCOME_LABEL: Record<string, string> = {
  COMPLETED: '完成（计薪）',
  STUDENT_ABSENT: '学生缺勤（计薪）',
  TEACHER_ABSENT: '老师缺勤（扣 3 节）',
}

/** 缺勤类型：双方缺勤在老师端显示老师缺勤、学生端显示学生缺勤，这里显示真实情况 */
const ABSENT_TYPE_LABEL: Record<string, string> = {
  TEACHER: '老师缺勤',
  STUDENT: '学生缺勤',
  BOTH: '双方缺勤',
}

const getUtcMonthTimestamp = (value: Dayjs) =>
  Date.UTC(value.year(), value.month(), 1, 0, 0, 0, 0)

const Index = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [teacherId, setTeacherId] = useState('')
  const [month, setMonth] = useState<Dayjs | null>(() => dayjs())
  const [details, setDetails] = useState<IAdminTeacherAttendanceDetailsRes | null>(null)
  const [records, setRecords] = useState<IAdminTeacherAttendanceDetailItem[]>([])
  const [validFilter, setValidFilter] = useState<'all' | 'valid' | 'invalid'>('all')

  useEffect(() => {
    const teacherIdParam = searchParams.get('teacherId')
    const monthParam = searchParams.get('month')
    if (teacherIdParam) {
      setTeacherId(teacherIdParam)
    }
    if (monthParam && Number.isFinite(Number(monthParam))) {
      setMonth(dayjs(Number(monthParam)))
    }
  }, [searchParams])

  const monthTimestamp = useMemo(() => {
    if (!month) return null
    return getUtcMonthTimestamp(month)
  }, [month])

  const fetchData = useCallback(() => {
    if (!teacherId) {
      message.warning('请输入教师ID')
      return
    }
    if (!monthTimestamp) {
      message.warning('请选择月份')
      return
    }
    setLoading(true)
    getClassRecordAttendanceDetails(teacherId, monthTimestamp)
      .then((res) => {
        const data = res.data as IAdminTeacherAttendanceDetailsRes
        setDetails(data || null)
        setRecords(data?.records || [])
      })
      .catch((err) => {
        message.error(err?.message || '请求错误，请稍后再试')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [monthTimestamp, teacherId])

  useEffect(() => {
    if (!teacherId) return
    fetchData()
  }, [fetchData, teacherId])

  const onSearch = useCallback(() => {
    if (!teacherId || !monthTimestamp) {
      fetchData()
      return
    }
    navigate(
      `/home/class-record-statistics/details?teacherId=${teacherId}&month=${monthTimestamp}`,
    )
    fetchData()
  }, [fetchData, monthTimestamp, navigate, teacherId])

  const filteredRecords = useMemo(() => {
    if (validFilter === 'all') return records
    const isValid = validFilter === 'valid'
    return records.filter((record) => record.validSession === isValid)
  }, [records, validFilter])

  const onExport = useCallback(() => {
    if (!filteredRecords.length) {
      message.warning('暂无可导出数据')
      return
    }

    const rows = filteredRecords.map((record) => ({
      '预约ID/Appointment ID': record.appointmentId,
      '学生ID/Student ID': record.studentId,
      '学生姓名/Student Name': record.studentName || '',
      '上课时间（老师时区）/Class Start (Teacher Time)': record.classStartLocal
        ?? (record.classStartTime ? dayjs(record.classStartTime).format('YYYY-MM-DD HH:mm:ss') : ''),
      '状态/Status': record.status,
      '录制ID/Class Record ID': record.classRecordId || '',
      '开始时间/Class Started At': record.classStartedAt
        ? dayjs(record.classStartedAt).format('YYYY-MM-DD HH:mm:ss')
        : '',
      '结束时间/Class Ended At': record.classEndedAt
        ? dayjs(record.classEndedAt).format('YYYY-MM-DD HH:mm:ss')
        : '',
      '老师在教室(秒)/Teacher In Class (s)': record.teacherInClassInSeconds ?? '',
      '计薪/Paid': record.validSession ? 'Yes' : 'No',
      '薪资结算/Salary Outcome': SALARY_OUTCOME_LABEL[record.salaryOutcome ?? ''] ?? '',
      '缺勤原因/Absent Reason': record.absentReason || '',
      '缺勤类型/Absent Type': ABSENT_TYPE_LABEL[record.absentType ?? ''] ?? (record.absentType || ''),
    }))

    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Details')

    const monthLabel = month ? month.format('YYYY-MM') : dayjs().format('YYYY-MM')
    const teacherLabel = details?.teacherName || teacherId || 'teacher'
    const fileName = `attendance-details-${teacherLabel}-${monthLabel}.xlsx`
    XLSX.writeFile(workbook, fileName)
  }, [details?.teacherName, filteredRecords, month, teacherId])

  const columns = useMemo<TableProps<IAdminTeacherAttendanceDetailItem>['columns']>(
    () => [
      {
        title: '预约ID/Appointment ID',
        dataIndex: 'appointmentId',
        key: 'appointmentId',
        width: 140,
        fixed: 'left',
      },
      {
        title: '学生ID/Student ID',
        dataIndex: 'studentId',
        key: 'studentId',
        width: 140,
      },
      {
        title: '学生姓名/Student Name',
        dataIndex: 'studentName',
        key: 'studentName',
        width: 160,
        render: (text) => text || '-',
      },
      {
        title: '上课时间（老师时区）/Class Start',
        dataIndex: 'classStartTime',
        key: 'classStartTime',
        width: 190,
        render: (text, record) => record.classStartLocal ?? (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
      },
      {
        title: '状态/Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
      },
      {
        title: '录制ID/Class Record ID',
        dataIndex: 'classRecordId',
        key: 'classRecordId',
        width: 140,
        render: (text) => text || '-',
      },
      {
        title: '开始时间/Class Started At',
        dataIndex: 'classStartedAt',
        key: 'classStartedAt',
        width: 170,
        render: (text) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
      },
      {
        title: '结束时间/Class Ended At',
        dataIndex: 'classEndedAt',
        key: 'classEndedAt',
        width: 170,
        render: (text) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'),
      },
      {
        title: '老师在教室(秒)/Teacher In Class (s)',
        dataIndex: 'teacherInClassInSeconds',
        key: 'teacherInClassInSeconds',
        width: 150,
        render: (text) => (text ?? '-'),
      },
      {
        title: '计薪/Paid',
        dataIndex: 'validSession',
        key: 'validSession',
        width: 100,
        render: (value) => (value ? '是' : '否'),
      },
      {
        title: '薪资结算/Salary Outcome',
        dataIndex: 'salaryOutcome',
        key: 'salaryOutcome',
        width: 150,
        render: (value) => SALARY_OUTCOME_LABEL[value ?? ''] ?? '-',
      },
      {
        title: '缺勤原因/Absent Reason',
        dataIndex: 'absentReason',
        key: 'absentReason',
        width: 160,
        render: (text) => text || '-',
      },
      {
        title: '缺勤类型/Absent Type',
        dataIndex: 'absentType',
        key: 'absentType',
        width: 160,
        render: (text) => ABSENT_TYPE_LABEL[text ?? ''] ?? (text || '-'),
      },
    ],
    [],
  )

  return (
    <Container>
      <div className="top">
        <Space wrap>
          <Button onClick={() => navigate(-1)}>返回</Button>
          <Input
            placeholder="教师ID"
            value={teacherId}
            onChange={(event) => setTeacherId(event.target.value.trim())}
            style={{ width: 200 }}
          />
          <DatePicker
            picker="month"
            allowClear={false}
            value={month}
            onChange={(value) => {
              setMonth(value)
            }}
          />
          <Select
            style={{ width: 180 }}
            value={validFilter}
            onChange={(value) => setValidFilter(value)}
            options={[
              { label: '全部/All', value: 'all' },
              { label: '计薪/Paid', value: 'valid' },
              { label: '不计薪/Unpaid', value: 'invalid' },
            ]}
          />
          <Button type="primary" loading={loading} onClick={onSearch}>
            查询
          </Button>
          <Button onClick={onExport}>导出/Export</Button>
          {details && (
            <Typography.Text type="secondary">
              {details.teacherName} · {details.month} · 月份与上课时间按老师时区 {details.timeZone || 'UTC'}，口径与薪资发放一致
            </Typography.Text>
          )}
        </Space>
      </div>
      <div className="table">
        <Table
          rowKey="appointmentId"
          scroll={{ x: 1400, y: 500 }}
          loading={loading}
          columns={columns}
          dataSource={filteredRecords}
          pagination={false}
        />
      </div>
    </Container>
  )
}

export default memo(Index)
